import { errorMessage } from '../lib/errors';
import { Button, Field, Notice } from './primitives';
import { useId, useRef, useState } from 'react';
import { Dialog } from './Dialog';

export interface PromptField {
  key: string; label: string; type?: 'text' | 'textarea'; placeholder?: string; defaultValue?: string; required?: boolean;
}
interface ModalDialogProps {
  isOpen: boolean; title: string; message?: string | undefined; type: 'alert' | 'confirm' | 'prompt';
  promptFields?: PromptField[] | undefined; confirmText?: string | undefined; cancelText?: string | undefined;
  onConfirm: (values?: Record<string, string>  ) => void | Promise<void>;
  onCancel: () => void;
}
export function ModalDialog(props: ModalDialogProps): import("react").JSX.Element | null {
  return props.isOpen ? <OpenModalDialog {...props} /> : null;
}
function OpenModalDialog({ title, message, type, promptFields = [], confirmText = type === 'alert' ? 'OK' : 'Confirm', cancelText = 'Cancel', onConfirm, onCancel }: ModalDialogProps): import("react").JSX.Element {
  const id = useId();
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(promptFields.map(field => [field.key, field.defaultValue ?? ''])));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const busy = useRef(false);
  return <Dialog label={title} onClose={onCancel} pending={pending}>
    <form className="modal" onSubmit={event => { void (async (): Promise<void> => {
      event.preventDefault();
      if (busy.current) return;
      busy.current = true; setPending(true); setError('');
      try { await onConfirm(type === 'prompt' ? values : undefined); }
      catch (reason) { setError(reason instanceof Error ? errorMessage(reason) : 'Unable to complete this action. Please try again.'); }
      finally { busy.current = false; setPending(false); }
    })(); }}>
      <h3>{title}</h3>
      {message && <p className="dialog-message">{message}</p>}
      <fieldset disabled={pending} className="dialog-fields">
        {type === 'prompt' && promptFields.map(field => {
          const fieldId = `${id}-${field.key}`;
          const common = { id: fieldId, value: values[field.key] ?? '', placeholder: field.placeholder, required: field.required !== false,
            onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => setValues(previous => ({ ...previous, [field.key]: event.target.value })) };
          return <Field key={field.key} label={field.label} htmlFor={fieldId}>
            {field.type === 'textarea' ? <textarea {...common} rows={4} /> : <input {...common} type="text" />}
          </Field>;
        })}
        {error && <Notice variant="danger">{error}</Notice>}
        <div className="actions">
          {type !== 'alert' && <Button variant="secondary" type="button"  onClick={onCancel}>{cancelText}</Button>}
          <Button variant="primary" type="submit">{pending ? 'Working…' : confirmText}</Button>
        </div>
      </fieldset>
    </form>
  </Dialog>;
}
